'use client'

import { motion } from 'framer-motion'
import type { ReactNode } from 'react'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'
import { cn } from '@/lib/utils'
import { type ProductVariant, productStyles } from './product-styles'
import { sectionIconMap, type SectionIconName } from './icon-map'

interface Audience {
  icon: SectionIconName
  label: string
}

interface ProductAudienceSectionProps {
  variant: ProductVariant
  eyebrow?: string
  title: ReactNode
  subtitle?: string
  audiences: Audience[]
  compact?: boolean
  embedded?: boolean
}

export function ProductAudienceSection({
  variant,
  eyebrow,
  title,
  subtitle,
  audiences,
  compact = false,
  embedded = false,
}: ProductAudienceSectionProps) {
  const styles = productStyles[variant]
  const Heading = embedded ? 'h3' : 'h2'

  const content = (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className={cn(
          embedded ? 'text-left' : 'text-center',
          !embedded && (compact ? 'mb-6' : 'mb-10')
        )}
      >
        {eyebrow && (
          <p className={`text-micro-uppercase ${styles.eyebrow} mb-3`}>{eyebrow}</p>
        )}
        <Heading className={cn(
          embedded ? 'text-xl md:text-2xl font-heading font-semibold leading-tight text-balance' :
            compact ? 'text-3xl md:text-4xl font-heading font-semibold leading-tight text-balance' : 'text-display-1',
          styles.title,
          !embedded && 'mb-4'
        )}>{title}</Heading>
        {subtitle && (
          <p className={`text-body-lg ${styles.subtitle} max-w-2xl mx-auto`}>
            {subtitle}
          </p>
        )}
      </motion.div>

      <div className={cn(
        'flex flex-wrap',
        embedded ? 'items-center' : 'justify-center max-w-3xl mx-auto',
        compact ? 'gap-3' : 'gap-4'
      )}>
        {audiences.map((item, index) => {
          const Icon = sectionIconMap[item.icon]
          if (!Icon) return null
          return (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className={`flex items-center gap-3 px-5 py-3 rounded-full border ${styles.card} ${styles.cardBorder} ${styles.cardText} transition-colors hover:opacity-90`}
            >
              <Icon className={`w-4 h-4 ${styles.iconColor}`} />
              <span className="text-sm font-medium">{item.label}</span>
            </motion.div>
          )
        })}
      </div>
    </>
  )

  return embedded ? (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-10">
      {content}
    </div>
  ) : (
    <Section
      background={styles.background}
      className={compact ? 'pt-12 pb-6 md:pt-16 md:pb-6 lg:pt-16 lg:pb-6' : undefined}
    >
      <Container>{content}</Container>
    </Section>
  )
}
